import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Phone, MapPin, Clock, CalendarCheck,
  Building2, ChevronRight, CheckCircle2, Users
} from "lucide-react";
import { getIcon, formatHours, track } from "@/components/landing/shared";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConocenosPage() {
  const [content, setContent] = useState(null);
  const [config, setConfig] = useState(null);
  const [centers, setCenters] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/site-content`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/config`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/centers?active_only=true`).then(r => r.data).catch(() => []),
    ]).then(([cnt, cfg, ctr]) => {
      setContent(cnt);
      setConfig(cfg);
      setCenters(ctr);
    });
  }, []);

  if (!content || !config) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  const cn = content.conocenos || {};
  const hero = cn.hero || {};
  const quienes = cn.quienes_somos || {};
  const trayectoria = cn.trayectoria || {};
  const queHacemos = cn.que_hacemos || {};
  const formaTrabajo = cn.forma_trabajar || {};
  const centrosSec = cn.centros || {};
  const confianza = cn.confianza || {};
  const ctaFinal = cn.cta_final || {};
  const seo = cn.seo || {};
  const companyName = config.company_name || "ASCAUTO";

  // SEO
  const pageTitle = seo.title || `Conocenos | ${companyName}`;
  const pageDesc = seo.description || `Conoce ${companyName}, tu taller de confianza con mas de 40 anos de experiencia.`;

  return (
    <div className="bg-white" data-testid="conocenos-page">
      {/* SEO Meta */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />

      {/* ============================================= */}
      {/* 1. HERO                                       */}
      {/* ============================================= */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden" data-testid="conocenos-hero">
        <div className="absolute inset-0">
          {hero.image_url ? (
            <img
              src={getImageUrl(hero.image_url)}
              alt="" className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-slate-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 to-slate-900/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 max-w-[40px] bg-red-600" />
              <span className="text-red-400 text-[11px] font-semibold uppercase tracking-[0.3em]">
                {hero.label || "Sobre nosotros"}
              </span>
            </div>
            <h1 className="heading-industrial text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[0.95] mb-6" data-testid="conocenos-h1">
              {hero.title || "Conócenos"}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              {hero.subtitle || `Descubre la historia, la filosofia y el equipo que hay detras de ${companyName}.`}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
      </section>

      {/* ============================================= */}
      {/* 2. QUIENES SOMOS                              */}
      {/* ============================================= */}
      <section className="py-20 lg:py-28" data-testid="conocenos-quienes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-1 bg-red-600" />
                <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">
                  {quienes.label || "Nuestra historia"}
                </span>
              </div>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mb-6" data-testid="quienes-title">
                {quienes.title || "Quienes somos"}
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                {(quienes.text || `${companyName} es un grupo de talleres oficiales con una larga trayectoria en el sector de la automocion. Desde nuestros inicios, hemos apostado por la calidad, la formacion continua y la atencion personalizada para ofrecer a nuestros clientes el mejor servicio.`).split("\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              {quienes.image_url ? (
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={getImageUrl(quienes.image_url)}
                    alt={quienes.title || "Quienes somos"}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center">
                  <Users className="w-24 h-24 text-slate-200" />
                </div>
              )}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-red-600 flex items-center justify-center">
                <span className="text-white text-2xl font-extrabold">{quienes.stat_value || "40+"}</span>
              </div>
              <p className="absolute -bottom-10 left-24 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {quienes.stat_label || "anos de experiencia"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* 3. TRAYECTORIA                                */}
      {/* ============================================= */}
      {(trayectoria.items || []).length > 0 && (
        <section className="py-20 lg:py-28 bg-slate-50" data-testid="conocenos-trayectoria">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">
                {trayectoria.label || "Nuestra trayectoria"}
              </span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mt-3">
                {trayectoria.title || "Trayectoria"}
              </h2>
              {trayectoria.subtitle && (
                <p className="text-sm text-slate-500 mt-3 max-w-xl mx-auto">{trayectoria.subtitle}</p>
              )}
            </div>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-slate-200 lg:-translate-x-px" />
              <div className="space-y-12">
                {(trayectoria.items || []).map((item, i) => (
                  <div key={i} className={`relative flex items-start gap-6 lg:gap-0 ${i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"}`} data-testid={`trayectoria-item-${i}`}>
                    {/* Dot */}
                    <div className="absolute left-4 lg:left-1/2 w-3 h-3 bg-red-600 rounded-full -translate-x-1.5 mt-1.5 z-10 ring-4 ring-white" />
                    {/* Content */}
                    <div className={`ml-12 lg:ml-0 lg:w-1/2 ${i % 2 === 0 ? "lg:pr-12 lg:text-right" : "lg:pl-12"}`}>
                      <span className="text-red-600 font-extrabold text-xl">{item.year}</span>
                      <h3 className="heading-industrial text-lg font-bold text-slate-900 uppercase mt-1">{item.title}</h3>
                      <p className="text-sm text-slate-500 mt-2 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 4. QUE HACEMOS                                */}
      {/* ============================================= */}
      {(queHacemos.items || []).length > 0 && (
        <section className="py-20 lg:py-28" data-testid="conocenos-que-hacemos">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">
                {queHacemos.label || "Nuestros servicios"}
              </span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mt-3">
                {queHacemos.title || "Que hacemos"}
              </h2>
              {queHacemos.subtitle && (
                <p className="text-sm text-slate-500 mt-3 max-w-xl mx-auto">{queHacemos.subtitle}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(queHacemos.items || []).map((item, i) => {
                const Icon = getIcon(item.icon);
                return (
                  <div key={i} className="group border border-slate-200 p-8 hover:border-red-200 hover:shadow-lg transition-all duration-300" data-testid={`que-hacemos-item-${i}`}>
                    <div className="w-14 h-14 bg-red-50 group-hover:bg-red-600 flex items-center justify-center mb-6 transition-colors duration-300">
                      <Icon className="w-7 h-7 text-red-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="heading-industrial text-lg font-bold text-slate-900 uppercase mb-3">{item.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 5. FORMA DE TRABAJAR                          */}
      {/* ============================================= */}
      {(formaTrabajo.items || []).length > 0 && (
        <section className="py-20 lg:py-28 bg-slate-900 relative overflow-hidden" data-testid="conocenos-forma-trabajar">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 60px)" }} />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-400 text-[11px] font-semibold uppercase tracking-[0.25em]">
                {formaTrabajo.label || "Nuestro proceso"}
              </span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase mt-3">
                {formaTrabajo.title || "Nuestra forma de trabajar"}
              </h2>
              {formaTrabajo.subtitle && (
                <p className="text-sm text-slate-400 mt-3 max-w-xl mx-auto">{formaTrabajo.subtitle}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {(formaTrabajo.items || []).map((item, i) => (
                <div key={i} className="relative text-center" data-testid={`forma-trabajar-item-${i}`}>
                  <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-6">
                    <span className="text-white text-2xl font-extrabold">{item.step || i + 1}</span>
                  </div>
                  <h3 className="heading-industrial text-base font-bold text-white uppercase mb-3">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
                  {i < (formaTrabajo.items || []).length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)]">
                      <div className="h-px bg-slate-700 w-full" />
                      <ChevronRight className="w-4 h-4 text-slate-600 absolute -right-2 -top-2" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 6. NUESTROS CENTROS                           */}
      {/* ============================================= */}
      {centers.length > 0 && (
        <section className="py-20 lg:py-28" data-testid="conocenos-centros">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">
                {centrosSec.label || "Donde estamos"}
              </span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mt-3">
                {centrosSec.title || "Nuestros centros"}
              </h2>
              {centrosSec.subtitle && (
                <p className="text-sm text-slate-500 mt-3 max-w-xl mx-auto">{centrosSec.subtitle}</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {centers.map((c, i) => (
                <div key={c.id} className="bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 group" data-testid={`conocenos-center-${i}`}>
                  <div className="h-52 overflow-hidden relative bg-slate-200">
                    {(c.image_url || c.map_image_url) ? (
                      <img src={getImageUrl(c.image_url || c.map_image_url)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full bg-slate-900 flex items-center justify-center">
                        <Building2 className="w-16 h-16 text-slate-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent" />
                    <div className="absolute bottom-5 left-6">
                      <h3 className="heading-industrial text-xl sm:text-2xl font-bold text-white uppercase">{c.name}</h3>
                    </div>
                  </div>
                  <div className="p-6 lg:p-7">
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{c.address}</p>
                          <p className="text-xs text-slate-500">{c.zip_code} {c.city}</p>
                        </div>
                      </div>
                      {c.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-red-600 flex-shrink-0" />
                          <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="text-sm text-slate-700 hover:text-red-600 transition-colors font-medium">{c.phone}</a>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <p className="text-sm text-slate-600">{formatHours(c.working_hours)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Link to="/cita" onClick={() => track("conocenos_cta_cita", c.name)}>
                        <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none text-[10px] uppercase tracking-wider font-bold h-10" data-testid={`conocenos-center-cta-${i}`}>
                          <CalendarCheck className="w-3.5 h-3.5 mr-1.5" /> Pedir cita
                        </Button>
                      </Link>
                      <a href={c.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${c.address} ${c.zip_code} ${c.city}`)}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="w-full border-slate-300 text-slate-700 rounded-none text-[10px] uppercase tracking-wider font-bold h-10 hover:bg-slate-50">
                          <MapPin className="w-3.5 h-3.5 mr-1.5" /> Como llegar
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 7. POR QUE CONFIAR                            */}
      {/* ============================================= */}
      {(confianza.items || []).length > 0 && (
        <section className="py-20 lg:py-28 bg-slate-50" data-testid="conocenos-confianza">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">
                {confianza.label || "Garantia de calidad"}
              </span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mt-3">
                {confianza.title || `Por que confiar en ${companyName}`}
              </h2>
              {confianza.subtitle && (
                <p className="text-sm text-slate-500 mt-3 max-w-xl mx-auto">{confianza.subtitle}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(confianza.items || []).map((item, i) => {
                const Icon = item.icon ? getIcon(item.icon) : CheckCircle2;
                return (
                  <div key={i} className="flex gap-5 p-6 bg-white border border-slate-200 hover:shadow-md transition-all" data-testid={`confianza-item-${i}`}>
                    <div className="w-12 h-12 bg-red-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="heading-industrial text-sm font-bold text-slate-900 uppercase mb-2">{item.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 8. CTA FINAL                                  */}
      {/* ============================================= */}
      <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden" data-testid="conocenos-cta-final">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 60px)" }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-8">
            <CalendarCheck className="w-8 h-8 text-white" />
          </div>
          <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase mb-5">
            {ctaFinal.title || "Ven a conocernos"}
          </h2>
          <p className="text-slate-400 mb-12 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
            {ctaFinal.subtitle || "Visita cualquiera de nuestros centros o reserva tu cita online. Estaremos encantados de atenderte."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={ctaFinal.button_link || "/cita"} onClick={() => track("conocenos_cta_final", "reservar_cita")}>
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-14 px-12 shadow-lg shadow-red-600/30 transition-all hover:shadow-xl" data-testid="conocenos-cta-btn">
                {ctaFinal.button_text || "Reservar Cita"} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
